import { useEffect, useState } from "react";
import axios from "axios";
import { Search, Link2, FileCheck, Lightbulb, Loader2, Plus, ExternalLink } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function InsightsPage() {
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState("");
  const [tab, setTab] = useState("schema");
  const [data, setData] = useState({ schema: null, linking: null, gap: null });
  const [loading, setLoading] = useState(false);
  const [selectedGaps, setSelectedGaps] = useState({});

  useEffect(() => {
    document.title = "Insights | Epidemos Admin";
    axios.get(`${BACKEND_URL}/api/sites?status=active`)
      .then(r => setSites(Array.isArray(r.data) ? r.data : r.data.sites || []))
      .catch(() => {});
  }, []);

  const run = async (key) => {
    if (!siteId) return;
    setLoading(true);
    try {
      let r;
      if (key === "schema") r = await axios.post(`${BACKEND_URL}/api/admin/schema-audit/site/${siteId}?sample_size=15`);
      else if (key === "linking") r = await axios.get(`${BACKEND_URL}/api/admin/internal-linking/health/${siteId}?sample_size=50`);
      else if (key === "gap") r = await axios.post(`${BACKEND_URL}/api/admin/content-gap/scan/${siteId}?max_keywords=5&max_competitors=3`);
      setData({ ...data, [key]: r.data });
    } finally { setLoading(false); }
  };

  const addGapsToQueue = async () => {
    const titles = Object.entries(selectedGaps).filter(([_, v]) => v).map(([k]) => k);
    if (!titles.length) return alert("Nic nie zaznaczono");
    const r = await axios.post(`${BACKEND_URL}/api/admin/content-gap/add-to-queue/${siteId}`, { titles, priority: "high" });
    alert(`Dodano ${r.data.inserted} tematów do agent_topic_queue`);
    setSelectedGaps({});
  };

  return (
    <div className="p-6 max-w-none mx-auto">
      <h1 className="text-2xl font-bold mb-2 flex items-center gap-2">
        <Lightbulb className="w-7 h-7 text-amber-600" /> Insights++
      </h1>
      <p className="text-sm text-slate-500 mb-4">Schema audit + Internal Linking Health + Content Gap Analyzer per portal.</p>

      <div className="mb-6 flex gap-2 items-end">
        <div className="flex-1">
          <label className="block text-xs uppercase text-slate-500 mb-1">Portal</label>
          <select value={siteId} onChange={e => setSiteId(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg">
            <option value="">— wybierz portal —</option>
            {sites.map(s => <option key={s.id} value={s.id}>{s.name || s.url}</option>)}
          </select>
        </div>
      </div>

      <div className="border-b border-slate-200 mb-4 flex gap-1">
        {[
          { id: "schema", label: "Schema Audit", icon: FileCheck },
          { id: "linking", label: "Internal Linking", icon: Link2 },
          { id: "gap", label: "Content Gap", icon: Search },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition flex items-center gap-1.5 ${
              tab === t.id ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-900"
            }`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {siteId && (
        <button onClick={() => run(tab)} disabled={loading}
          className="mb-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg inline-flex items-center gap-1.5 disabled:opacity-50">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          Uruchom analizę
        </button>
      )}

      {/* SCHEMA TAB */}
      {tab === "schema" && data.schema?.ok && (
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <p className="text-sm text-slate-500 mb-3">Audited {data.schema.audited} / failed {data.schema.failed}</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {Object.entries(data.schema.schema_percentages || {}).map(([k, v]) => (
              <div key={k} className="p-3 bg-slate-50 rounded">
                <div className="text-xs text-slate-500">{k}</div>
                <div className={`text-xl font-bold ${v >= 80 ? "text-green-600" : v >= 40 ? "text-amber-600" : "text-red-600"}`}>{v}%</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* LINKING TAB */}
      {tab === "linking" && data.linking?.ok && (
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="grid grid-cols-4 gap-3 mb-4">
            <div className="p-3 bg-blue-50 rounded">
              <div className="text-xs text-blue-700">Avg outgoing</div>
              <div className="text-2xl font-bold text-blue-700">{data.linking.avg_outgoing}</div>
            </div>
            <div className="p-3 bg-purple-50 rounded">
              <div className="text-xs text-purple-700">Avg incoming</div>
              <div className="text-2xl font-bold text-purple-700">{data.linking.avg_incoming}</div>
            </div>
            <div className="p-3 bg-red-50 rounded">
              <div className="text-xs text-red-700">Orphan posts</div>
              <div className="text-2xl font-bold text-red-700">{data.linking.orphan_count}</div>
            </div>
            <div className="p-3 bg-green-50 rounded">
              <div className="text-xs text-green-700">Well-linked (5+ out)</div>
              <div className="text-2xl font-bold text-green-700">{data.linking.well_linked_count}</div>
            </div>
          </div>
          {data.linking.orphan_sample?.length > 0 && (
            <div>
              <h3 className="font-bold text-sm mb-2 text-red-700">Orphan posts (0 linków przychodzących):</h3>
              <ul className="text-xs space-y-1">
                {data.linking.orphan_sample.map((o, i) => (
                  <li key={i}><a href={o.url} target="_blank" rel="noopener" className="text-blue-600 hover:underline">{o.title}</a></li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* GAP TAB */}
      {tab === "gap" && data.gap?.ok && (
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="mb-3 text-sm text-slate-600">
            Keywords scanned: {data.gap.keywords_scanned?.join(", ")}<br />
            Top competitors: {data.gap.competitors_top?.join(", ")}<br />
            Our titles in DB: {data.gap.our_titles_count} | Gaps found: <strong className="text-red-700">{data.gap.gaps_found}</strong>
          </div>
          <h3 className="font-bold text-sm mb-2">Tematy które rankują konkurenci, a my NIE mamy:</h3>
          <div className="max-h-96 overflow-y-auto space-y-1 mb-3">
            {(data.gap.gaps_sample || []).map((g, i) => (
              <label key={i} className="flex items-start gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer text-sm">
                <input type="checkbox" checked={!!selectedGaps[g.sample_title]}
                  onChange={e => setSelectedGaps({ ...selectedGaps, [g.sample_title]: e.target.checked })} />
                <div className="flex-1">
                  <div className="font-medium text-slate-900">{g.sample_title}</div>
                  <div className="text-xs text-slate-500">źródło: {g.source_competitor}</div>
                </div>
              </label>
            ))}
          </div>
          {Object.values(selectedGaps).some(Boolean) && (
            <button onClick={addGapsToQueue}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded inline-flex items-center gap-1.5">
              <Plus className="w-4 h-4" /> Dodaj zaznaczone do topic queue ({Object.values(selectedGaps).filter(Boolean).length})
            </button>
          )}
        </div>
      )}

      {data[tab] && !data[tab].ok && (
        <div className="p-4 bg-red-50 border border-red-200 rounded text-sm text-red-700">{data[tab].error || data[tab].message}</div>
      )}
    </div>
  );
}
